// External Imports
import { Fragment, useContext, useState, useEffect } from "react";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import SelectPair from "../UI/SelectPair";
import { DataContext } from "../../context/data-context";
import Modal from "../UI/Modal";
import { MdCheck, MdClose } from "react-icons/md";
import LinkPair from "../UI/LinkPair";
import { requestAdminAPI } from "../../api/posh-api";
import uploadFile from "../../util/upload-file";
import LoadingSpinner from "../UI/LoadingSpinner";
import { UtilityContext } from "../../context/util-context";
import permissions from "./permission-presets/porter.json"
import { validateContractFile } from "../../util/validate-file";
import Departments from "./departments.json";

// Internal Imports

/** ========================================================================
 * * Add Employee Modal
 * This component renders a page add an employee.
 * ========================================================================== */

export default function CreateEmployee({ open, setOpen }) {
  // Load Context
  const { locations } = useContext(DataContext);
  const { navigate } = useContext(UtilityContext);
  // Initialze States
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [fileError, setFileError] = useState("");

  /* ====================== EMAIL VALIDATION ====================== */
  const validateEmail = (email) => {
    if (!email) {
      setEmailError("");
      return true;
    }
    if (!email.endsWith("@poshcars.io")) {
      setEmailError("Email must end with @poshcars.io");
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleEmailChange = (e) => {
    const email = e.target.value;
    validateEmail(email);
  };

  // Clear errors when modal closes
  useEffect(() => {
    if (!open) {
      setEmailError("");
      setFileError("");
    }
  }, [open]);

  /* ====================== ADD/EDIT EMPLOYEE ====================== */
  const submitHandler = async () => {
    try {
      const email = document.getElementById("email").value;
      if (!validateEmail(email)) {
        return;
      }
      
      const contractFile = document.getElementById("contract")?.files[0];
      const fileValidation = validateContractFile(contractFile);
      if (!fileValidation.isValid) {
        setFileError(fileValidation.error);
        return;
      }
      
      const payload = {
        name: document.getElementById("name").value,
        email: email,
        phone: document.getElementById("phone").value,
        type: document.getElementById("type").value?.toLowerCase(),
        title: document.getElementById("title").value,
        compensationFrequency: document.getElementById("compensationFrequency")
          .value,
        compensationAmount:
          parseFloat(document.getElementById("compensationAmount").value) || 0,
        locationId: document.getElementById("locationId").value,
        active: document.getElementById("status").value === "Active",
        permissions: [],
      };
      Object.keys(permissions).forEach((key) => {
          payload.permissions.push({ endpointId: key, allow: permissions[key] });
      });
      setLoading(true);
      const files = [
        {
          name: "contract",
          file: contractFile,
        },
      ];
      for (const file of files) {
        if (!file.file) continue;
        const url = await uploadFile(
          `employees/${payload.name}-${new Date().getTime()}`,
          file.name,
          file.file
        );
        payload[file.name] = url;
      }
      const employeeId = await requestAdminAPI("add-employee", payload);
      navigate(`/employees/info?id=${employeeId}`);
      setOpen(false);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal  lg:w-[900px]">
        <Heading
          title="Add Employee"
          isBackArrow={false}
          text="Please note that upon create the employee will receive credentials to the admin dashboard via email and sms."
        >
          {loading ? (
            <LoadingSpinner classes="text-white w-5 h-5" />
          ) : (
            <Fragment>
              <MdClose className="icon" onClick={() => setOpen(false)} />
              <MdCheck className="icon" onClick={submitHandler} />
            </Fragment>
          )}
        </Heading>

        <div className="items-end w-full gap-x-8 info lg:grid-cols-3">
          <DetailPair
            label="Full Name"
            edit={true}
            inputOptions={{
              required: true,
              id: "name",
            }}
          />
          <div>
            <DetailPair
              label="Email Address"
              edit={true}
              inputOptions={{
                required: true,
                type: "email",
                id: "email",
                onChange: handleEmailChange,
                className: emailError 
                  ? "w-full form-input border-red-600 focus:ring-red-600" 
                  : "w-full form-input",
              }}
            />
            {emailError && (
              <p className="mt-1 warning">{emailError}</p>
            )}
          </div>
          <DetailPair
            label="Phone Number"
            edit={true}
            inputOptions={{
              required: true,
              id: "phone",
            }}
          />
          <SelectPair
            label="Department"
            options={Departments.departments}
            edit={true}
            inputOptions={{
              id: "type",
              required: true,
            }}
          />
          <DetailPair
            label="Title"
            edit={true}
            inputOptions={{
              required: true,
              id: "title",
            }}
          />
          <SelectPair
            label="Location"
            options={locations.map((location) => ({
              id: location.id,
              name: location.name,
            }))}
            edit={true}
            inputOptions={{
              id: "locationId",
              required: true,
            }}
          />
          <SelectPair
            label="Compensation Frequency"
            options={["Hourly", "Weekly", "Bi-Weekly", "Monthly", "Per-diem"]}
            edit={true}
            inputOptions={{
              id: "compensationFrequency",
            }}
          />
          <DetailPair
            label="Compensation Amount"
            edit={true}
            inputOptions={{
              id: "compensationAmount",
              type: "number",
              step: "0.01",
            }}
          />
          <SelectPair
            label="Status"
            options={["Active", "Inactive"]}
            edit={true}
            inputOptions={{
              id: "status",
            }}
          />
          <div>
            <LinkPair
              label="Contract"
              edit={true}
              inputOptions={{
                type: "file",
                id: "contract",
                accept: ".pdf,.docx,.doc,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/jpg,image/webp,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/msword",
                onChange: (e) => {
                  const file = e.target.files[0];
                  const validation = validateContractFile(file);
                  if (!validation.isValid) {
                    setFileError(validation.error);
                  } else {
                    setFileError("");
                  }
                },
              }}
            />
            {fileError && (
              <p className="mt-1 warning">{fileError}</p>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}
